import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAgents } from "@/api/queries/agents";
import { useRequestConnection } from "@/api/mutations/connections";
import type { Agent } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceAgent: Agent;
}

export function RequestConnectionDialog({ open, onOpenChange, sourceAgent }: Props) {
  const agentsQuery = useAgents();
  const mutation = useRequestConnection();

  const [targetId, setTargetId] = useState<string>("");
  const [reason, setReason] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  // Self ausfiltern + nur nicht-revoked
  const candidates = (agentsQuery.data ?? []).filter(
    (a) => a.id !== sourceAgent.id && a.revokedAt === null,
  );

  function handleSubmit() {
    if (!targetId) return;
    setServerError(null);
    mutation.mutate(
      {
        requesterAgentId: sourceAgent.id,
        targetAgentId: targetId,
        reason: reason.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success("Connection-Request gesendet");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Request fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="request-connection-dialog">
        <DialogHeader>
          <DialogTitle>Connection anfragen</DialogTitle>
          <DialogDescription>
            Sendet einen Pending-Request an den Target-Owner. Erst nach Accept wird die
            Connection aktiv und beide Agents können sich gegenseitig via mesh.send adressieren.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Source-Agent</Label>
            <div className="text-sm font-mono text-subtext0">{sourceAgent.id}</div>
          </div>

          <div className="space-y-1">
            <Label>Target-Agent</Label>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger data-testid="request-conn-target">
                <SelectValue placeholder="— Agent wählen" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} ({a.id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {candidates.length === 0 && (
              <div className="text-xs text-subtext0">Keine anderen Agents verfügbar.</div>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="conn-reason">Reason (optional, max 500)</Label>
            <Textarea
              id="conn-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              data-testid="request-conn-reason"
            />
          </div>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="request-conn-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="request-conn-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!targetId || mutation.isPending}
            data-testid="request-conn-submit"
          >
            {mutation.isPending ? "..." : "Request senden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
